import { useCallback, useEffect, useState } from "react";
import { motion } from "motion/react";
import { CliPairingUserCodeSchema, formatCliPairingCode, type CliPairingView } from "@baton/contracts";
import { ApiError, type DashboardApi } from "./api.js";
import { usePrefersReducedMotion } from "./flow/reduced-motion.js";

/** The one path this page owns; the GitHub callback's `next` hop returns the visitor here. */
export const PAIR_PATH = "/pair";

/**
 * A typed or pasted code in either form (`abcd-2345`, `ABCD2345`), normalised the way the
 * server digests it. Returns null when it is not a Baton pairing code at all.
 */
export function parsePairCode(raw: string): string | null {
  const parsed = CliPairingUserCodeSchema.safeParse(raw.trim());
  return parsed.success ? parsed.data : null;
}

/**
 * The code the terminal put in the fragment. Fragments never reach the server, so this is the
 * one hop that can carry it; the caller strikes it out of the address bar immediately after.
 */
export function readPairCode(url: URL): string | null {
  if (url.pathname !== PAIR_PATH) return null;
  return parsePairCode(url.hash.replace(/^#/, ""));
}

/** Plain words for how long the terminal will keep waiting. */
export function expiresInLabel(expiresAt: string, now = Date.now()): string {
  const remaining = Date.parse(expiresAt) - now;
  if (!Number.isFinite(remaining) || remaining <= 0) return "expired";
  const minutes = Math.ceil(remaining / 60_000);
  return minutes <= 1 ? "expires in under a minute" : `expires in ${minutes} minutes`;
}

/** Codes are short-lived and single use, so every failure reads as "ask the terminal again". */
export function pairErrorMessage(caught: unknown): string {
  if (caught instanceof ApiError) {
    if (caught.code === "pair_not_found") return "That code has expired or was already used. Run the command again in your terminal.";
    if (caught.code === "pair_already_decided") return "This code was already answered. Run the command again in your terminal.";
    if (caught.code === "invalid_cli_pairing_code") return "That is not a pairing code. It is eight characters, like ABCD-2345.";
    if (caught.code === "pair_rate_limited") return "Too many attempts. Wait a minute, then try again.";
  }
  return caught instanceof Error ? caught.message : "Could not read the pairing request";
}

/** Which decision was taken, once one has been. */
export type PairOutcome = "approved" | "denied";

export interface PairPageProps {
  api: DashboardApi;
  userHandle: string;
  /** Code lifted from the fragment before it was stripped; held in memory only, never stored. */
  initialCode: string | null;
  /** Leaves the pairing route for the cockpit; `outcome` is null when the visitor just walked away. */
  onDone(outcome: PairOutcome | null): void;
}

/**
 * `/pair`: a terminal printed a code, this page approves it. The code lives in React state for
 * the length of the visit — never in the address bar after the first paint, never in storage.
 */
export function PairPage(props: PairPageProps) {
  const { api } = props;
  const reduced = usePrefersReducedMotion();
  const [code, setCode] = useState<string | null>(props.initialCode);
  const [typed, setTyped] = useState("");
  const [view, setView] = useState<CliPairingView | null>(null);
  const [outcome, setOutcome] = useState<PairOutcome | null>(null);
  const [loading, setLoading] = useState(false);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reading never consumes the code, so this may run again after a failed decision.
  useEffect(() => {
    if (!code || outcome) return;
    let cancelled = false;
    setLoading(true);
    api.readCliPairing(code)
      .then((next) => { if (!cancelled) { setView(next); setError(null); } })
      .catch((caught: unknown) => {
        if (cancelled) return;
        // Back to the field: a stale link is the common case, and retyping is the way out.
        setView(null);
        setCode(null);
        setError(pairErrorMessage(caught));
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [api, code, outcome]);

  const decide = useCallback(async (kind: PairOutcome) => {
    if (!code) return;
    setWorking(true); setError(null);
    try {
      if (kind === "approved") await api.approveCliPairing(code);
      else await api.denyCliPairing(code);
      setOutcome(kind);
    } catch (caught) {
      setError(pairErrorMessage(caught));
    } finally {
      setWorking(false);
    }
  }, [api, code]);

  function submitCode(event: React.FormEvent) {
    event.preventDefault();
    const parsed = parsePairCode(typed);
    if (!parsed) { setError("That is not a pairing code. It is eight characters, like ABCD-2345."); return; }
    setError(null); setTyped(""); setCode(parsed);
  }

  // A code lifted from the fragment resolves before first paint, so the form never flashes.
  const phase: "approved" | "denied" | "confirm" | "checking" | "code" = outcome
    ?? (view && code ? "confirm" : code ? "checking" : "code");
  const enter = reduced ? false : { opacity: 0, y: 6 };

  return <main className="pair-shell" data-testid="pair-page">
    <motion.section
      key={phase}
      className="pair-card"
      initial={enter}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: reduced ? 0 : 0.22, ease: [0.4, 0, 0.2, 1] }}
    >
      <div className="wordmark small">baton<span>:</span></div>
      {phase === "approved" && <div className="pair-done" data-testid="pair-approved">
        <h1>Terminal connected.</h1>
        <p className="intro">{view ? `${view.label} can now act as @${props.userHandle}.` : `That terminal can now act as @${props.userHandle}.`}</p>
        <p className="muted compact">Next: finish setting up your project. The checklist opens on the canvas.</p>
        <button type="button" className="primary" data-testid="pair-continue" onClick={() => props.onDone("approved")}>Continue</button>
      </div>}

      {phase === "denied" && <div className="pair-done" data-testid="pair-denied">
        <h1>Nothing was connected.</h1>
        <p className="intro">The terminal was turned away. If that was a surprise, someone else has your code — do not approve it.</p>
        <button type="button" className="secondary" data-testid="pair-continue" onClick={() => props.onDone("denied")}>Back to Baton</button>
      </div>}

      {phase === "confirm" && view && <div className="pair-confirm" data-testid="pair-confirm">
        <h1>Connect this terminal?</h1>
        <dl className="pair-facts">
          <div><dt>Device</dt><dd data-testid="pair-label">{view.label}</dd></div>
          <div><dt>Code</dt><dd className="pair-code" data-testid="pair-code">{formatCliPairingCode(code ?? "")}</dd></div>
          <div><dt>Valid</dt><dd data-testid="pair-expires">{expiresInLabel(view.expiresAt)}</dd></div>
        </dl>
        <p className="pair-warning" data-testid="pair-warning">Only approve this if you just ran the command in your own terminal.</p>
        {view.status !== "pending" && <p className="muted compact">This code was already {view.status}.</p>}
        {error && <p className="error">{error}</p>}
        <div className="pair-actions">
          <button
            type="button"
            className="primary"
            data-testid="pair-approve"
            disabled={working || view.status !== "pending"}
            onClick={() => void decide("approved")}
          >{working ? "Connecting…" : "Connect this terminal"}</button>
          <button
            type="button"
            className="secondary"
            data-testid="pair-deny"
            disabled={working}
            onClick={() => void decide("denied")}
          >Not this time</button>
        </div>
      </div>}

      {phase === "checking" && <div className="pair-entry" data-testid="pair-checking">
        <h1>Checking that code…</h1>
        <p className="intro">A terminal on this machine wants to connect to your Baton account.</p>
      </div>}

      {phase === "code" && <div className="pair-entry" data-testid="pair-entry">
        <h1>Connect your terminal.</h1>
        <p className="intro">A terminal on this machine wants to connect to your Baton account. Type the code it printed.</p>
        <form onSubmit={submitCode} autoComplete="off">
          <label>Pairing code
            <input
              className="pair-input"
              data-testid="pair-input"
              value={typed}
              onChange={(event) => setTyped(event.target.value)}
              placeholder="ABCD-2345"
              maxLength={24}
              autoFocus
              autoComplete="off"
              autoCapitalize="characters"
              spellCheck={false}
              required
            />
          </label>
          {error && <p className="error" data-testid="pair-error">{error}</p>}
          <button type="submit" className="primary" disabled={loading}>{loading ? "Checking…" : "Continue"}</button>
        </form>
        <p className="pair-foot">
          <button type="button" className="link" data-testid="pair-skip" onClick={() => props.onDone(null)}>Skip for now</button>
          <span className="muted">The code is eight characters and lasts ten minutes.</span>
        </p>
      </div>}
    </motion.section>
  </main>;
}
